import React, { useState, useEffect } from 'react';
import { StoreSiteSection, StoreSiteSectionCreate, StoreSiteSectionUpdate } from '../../../types/site';
import { siteService } from '../../../services/siteService';
import { authService } from '../../../services/auth/authService';
import Modal from '../../shared/Modal';

const SiteSectionsCard: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [sections, setSections] = useState<StoreSiteSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<StoreSiteSection | null>(null);
  const [editForm, setEditForm] = useState({ name: '', description: '', active: true });
  const [saving, setSaving] = useState(false);

  // Add modal state
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', description: '', active: true });

  const storeId = authService.getStoreId();

  useEffect(() => {
    loadSections();
  }, []);

  const loadSections = async () => {
    try {
      setLoading(true);
      const data = await siteService.getStoreSiteSections();
      setSections(data);
      setError(null);
    } catch (err) {
      setError('Failed to load site sections');
      console.error('Error loading site sections:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (section: StoreSiteSection) => {
    setEditingSection(section);
    setEditForm({
      name: section.name,
      description: section.description || '',
      active: section.active
    });
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingSection) return;

    if (!editForm.name.trim()) {
      alert('Section name is required');
      return;
    }

    try {
      setSaving(true);
      const updateData: StoreSiteSectionUpdate = {
        name: editForm.name.trim(),
        description: editForm.description.trim() || undefined,
        active: editForm.active
      };
      await siteService.updateStoreSiteSection(editingSection.id, updateData);
      await loadSections();
      setEditModalOpen(false);
      setEditingSection(null);
    } catch (err) {
      console.error('Error updating site section:', err);
      alert('Failed to update site section. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingSection(null);
  };

  const handleDelete = async (sectionId: number, sectionName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${sectionName}"? This cannot be undone.`)) {
      return;
    }
    try {
      await siteService.deleteStoreSiteSection(sectionId);
      await loadSections();
    } catch (err) {
      console.error('Error deleting site section:', err);
      alert('Failed to delete. The section may be in use by existing sites.');
    }
  };

  const handleOpenAddModal = () => {
    setAddForm({ name: '', description: '', active: true });
    setAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setAddModalOpen(false);
  };

  const handleSaveAdd = async () => {
    if (!addForm.name.trim()) {
      alert('Section name is required');
      return;
    }

    try {
      setSaving(true);
      const createData: StoreSiteSectionCreate = {
        store_id: storeId,
        name: addForm.name.trim(),
        description: addForm.description.trim() || undefined,
        active: addForm.active
      };
      await siteService.createStoreSiteSection(createData);
      await loadSections();
      setAddModalOpen(false);
    } catch (err) {
      console.error('Error creating site section:', err);
      alert('Failed to create section. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="config-card">
      <div
        className="config-card-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="config-card-title">
          <span className="config-card-icon">📍</span>
          <h2>Site Sections</h2>
        </div>
        <div className="config-card-header-right">
          <span className="config-card-db-label">campground_db/store_site_sections</span>
          <span className={`config-card-toggle ${isExpanded ? 'expanded' : ''}`}>
            ▼
          </span>
        </div>
      </div>

      {isExpanded && (
        <div className="config-card-content">
          {loading ? (
            <div className="config-loading">Loading site sections...</div>
          ) : error ? (
            <div className="config-error">{error}</div>
          ) : sections.length === 0 ? (
            <div className="config-empty">
              <p>No sections configured. Create sections to organize sites by location.</p>
              <button className="btn btn-primary btn-sm" onClick={handleOpenAddModal}>+ Add Section</button>
            </div>
          ) : (
            <>
              <table className="config-table">
                <thead>
                  <tr>
                    <th>Section Name</th>
                    <th>Description</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sections.map(section => (
                    <tr key={section.id}>
                      <td>{section.name}</td>
                      <td>{section.description || '-'}</td>
                      <td className={section.active ? 'status-active' : 'status-inactive'}>
                        {section.active ? 'Active' : 'Inactive'}
                      </td>
                      <td>
                        <div className="config-actions">
                          <button
                            className="btn btn-text btn-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(section);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-text btn-sm btn-danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(section.id, section.name);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="config-card-footer">
                <button className="btn btn-primary btn-sm" onClick={handleOpenAddModal}>+ Add Section</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Edit Modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={handleCloseEditModal}
        title={`Edit Section: ${editingSection?.name || ''}`}
        size="small"
        footer={
          <>
            <button className="btn btn-secondary" onClick={handleCloseEditModal} disabled={saving}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSaveEdit} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </>
        }
      >
        <div className="edit-form">
          <div className="form-group">
            <label htmlFor="section-name">Section Name *</label>
            <input
              id="section-name"
              type="text"
              value={editForm.name}
              onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter section name"
            />
          </div>
          <div className="form-group">
            <label htmlFor="section-description">Description</label>
            <textarea
              id="section-description"
              value={editForm.description}
              onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Enter description (optional)"
              rows={3}
            />
          </div>
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={editForm.active}
                onChange={(e) => setEditForm(prev => ({ ...prev, active: e.target.checked }))}
              />
              Active
            </label>
            <small className="form-help">Inactive sections will not be available for new sites.</small>
          </div>
        </div>
      </Modal>

      {/* Add Modal */}
      <Modal
        isOpen={addModalOpen}
        onClose={handleCloseAddModal}
        title="Add Section"
        size="small"
        footer={
          <>
            <button className="btn btn-secondary" onClick={handleCloseAddModal} disabled={saving}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSaveAdd} disabled={saving}>
              {saving ? 'Adding...' : 'Add Section'}
            </button>
          </>
        }
      >
        <div className="edit-form">
          <div className="form-group">
            <label htmlFor="add-section-name">Section Name *</label>
            <input
              id="add-section-name"
              type="text"
              value={addForm.name}
              onChange={(e) => setAddForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Lakeside, Hilltop, Loop A"
            />
          </div>
          <div className="form-group">
            <label htmlFor="add-section-description">Description</label>
            <textarea
              id="add-section-description"
              value={addForm.description}
              onChange={(e) => setAddForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Enter description (optional)"
              rows={3}
            />
          </div>
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={addForm.active}
                onChange={(e) => setAddForm(prev => ({ ...prev, active: e.target.checked }))}
              />
              Active
            </label>
            <small className="form-help">Set as active to make this section available for new sites.</small>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SiteSectionsCard;
